import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Eye, 
  Package,
  User,
  DollarSign,
  Calendar,
  MapPin
} from 'lucide-react';
import toast from 'react-hot-toast';

const AdminOrdersPage = () => {
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, searchTerm, selectedStatus]);

  // Auto-open order modal if an orderId is provided in the query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderIdToOpen = params.get('orderId');
    if (!orderIdToOpen) return;

    if (orders && orders.length > 0) {
      const found = orders.find(o => String(o.id) === String(orderIdToOpen));
      if (found) {
        setSelectedOrder(found);
        setShowOrderModal(true);
      }
    }
  }, [location.search, orders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        status: selectedStatus
      });

      const response = await fetch(`/api/admin/orders?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
        setTotalPages(data.pagination.pages);
      } else {
        toast.error('Erreur lors du chargement des commandes');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [orderId]: true }));
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Statut de la commande mis à jour');
        fetchOrders();
      } else {
        toast.error('Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { label: 'En attente', class: 'bg-yellow-100 text-yellow-800' },
      'confirmed': { label: 'Confirmée', class: 'bg-blue-100 text-blue-800' },
      'processing': { label: 'En traitement', class: 'bg-purple-100 text-purple-800' },
      'shipped': { label: 'Expédiée', class: 'bg-indigo-100 text-indigo-800' },
      'delivered': { label: 'Livrée', class: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'Annulée', class: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };



  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredOrders = orders.filter(order =>
    order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Gestion des Commandes</h1>
        <p className="text-gray-400 mt-2">Suivez et gérez toutes les commandes de vos clients</p>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Recherche</label>
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Numéro, client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Statut</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="confirmed">Confirmée</option>
              <option value="processing">En traitement</option>
              <option value="shipped">Expédiée</option>
              <option value="delivered">Livrée</option>
              <option value="cancelled">Annulée</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedStatus('');
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors duration-200"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">#</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Commande</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Client</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Montant</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Statut</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredOrders.map((order, index) => (
                <motion.tr
                  key={order.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-700 transition-colors"
                >
                  <td className="py-3 px-4 text-gray-400">
                    {(currentPage - 1) * 20 + index + 1}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center">
                        <Package size={20} className="text-white" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{order.order_number}</div>
                        <div className="text-gray-400 text-sm">{order.items_count} article{order.items_count !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                        <User size={16} className="text-gray-300" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{order.customer_name}</div>
                        <div className="text-gray-400 text-sm">{order.customer_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-white font-medium">
                      {Number(order.total_amount || 0).toFixed(2)} €
                    </div>
                    <div className="text-gray-400 text-sm">{order.payment_method}</div>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowStatusModal(true);
                      }}
                      className={`w-24 h-8 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer hover:scale-105 flex items-center justify-center ${
                        order.status === 'pending' ? 'bg-yellow-500 text-gray-900' :
                        order.status === 'confirmed' ? 'bg-blue-500 text-white' :
                        order.status === 'processing' ? 'bg-purple-500 text-white' :
                        order.status === 'shipped' ? 'bg-indigo-500 text-white' :
                        order.status === 'delivered' ? 'bg-green-500 text-white' :
                        order.status === 'cancelled' ? 'bg-red-500 text-white' :
                        'bg-gray-500 text-white'
                      }`}
                      title="Cliquer pour changer le statut"
                    >
                      {getStatusLabel(order.status)}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-white">{formatDate(order.created_at)}</div>
                    <div className="text-gray-400 text-sm">
                      {order.updated_at !== order.created_at && `Modifiée: ${formatDate(order.updated_at)}`}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowOrderModal(true);
                      }}
                      className="text-blue-400 hover:text-blue-300 transition-colors p-2"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredOrders.length === 0 && (
        <div className="text-center py-12">
          <Package size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-400 mb-2">
            {searchTerm || selectedStatus ? 'Aucune commande trouvée' : 'Aucune commande'}
          </h3>
          <p className="text-gray-500">
            {searchTerm || selectedStatus 
              ? 'Essayez de modifier vos critères de recherche'
              : 'Les commandes apparaîtront ici une fois que vos clients commenceront à passer des commandes'
            }
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Précédent
            </button>
            <span className="px-4 py-2 text-gray-300">
              Page {currentPage} sur {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedOrder(null);
          }}
        />
      )}

      {/* Status Change Modal */}
      {showStatusModal && selectedOrder && (
        <StatusChangeModal
          order={selectedOrder}
          onClose={() => {
            setShowStatusModal(false);
            setSelectedOrder(null);
          }}
          onStatusChange={updateOrderStatus}
        />
      )}
    </div>
  );
};

// Helper function for status labels
const getStatusLabel = (status) => {
  const statusLabels = {
    'pending': 'En attente',
    'confirmed': 'Confirmée',
    'processing': 'En traitement',
    'shipped': 'Expédiée',
    'delivered': 'Livrée',
    'cancelled': 'Annulée'
  };
  return statusLabels[status] || status;
};

// Order Details Modal Component
const OrderDetailsModal = ({ order, onClose }) => {
  const [details, setDetails] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadOrderDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/admin/orders/${order.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch order details');
        }
        
        const data = await response.json();
        setDetails(data.order || order);
        setItems(data.items || []);
      } catch (error) {
        console.error('Error loading order details:', error);
        // Fallback to the order data we have
        setDetails(order);
        setItems(order.items || []);
      } finally {
        setLoading(false);
      }
    };

    loadOrderDetails();
  }, [order.id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
        <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-700">
            <div className="h-6 w-64 bg-gray-700 rounded animate-pulse" />
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-700 rounded-lg p-4">
                  <div className="h-4 w-20 bg-gray-600 rounded mb-3 animate-pulse" />
                  <div className="space-y-2">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="h-3 bg-gray-600 rounded animate-pulse" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="h-4 w-32 bg-gray-600 rounded mb-4 animate-pulse" />
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-gray-600 rounded animate-pulse" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const orderData = details || order;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">Détails de la commande #{orderData.order_number}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Order Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <Package size={20} className="text-yellow-500" />
                <h3 className="text-lg font-semibold text-white">Informations</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Statut:</span>
                  <span className="text-white">{orderData.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Date:</span>
                  <span className="text-white">{formatDate(orderData.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Méthode:</span>
                  <span className="text-white">{orderData.payment_method}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <User size={20} className="text-blue-500" />
                <h3 className="text-lg font-semibold text-white">Client</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Nom:</span>
                  <span className="text-white">{orderData.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Email:</span>
                  <span className="text-white">{orderData.customer_email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Téléphone:</span>
                  <span className="text-white">{orderData.customer_phone || 'Non fourni'}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <DollarSign size={20} className="text-green-500" />
                <h3 className="text-lg font-semibold text-white">Montant</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Sous-total:</span>
                  <span className="text-white">{Number(orderData.subtotal || 0).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Taxes:</span>
                  <span className="text-white">{Number(orderData.tax_amount || 0).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Livraison:</span>
                  <span className="text-white">{Number(orderData.shipping_cost || 0).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between border-t border-gray-600 pt-2">
                  <span className="text-white font-semibold">Total:</span>
                  <span className="text-white font-semibold">{Number(orderData.total_amount || 0).toFixed(2)} €</span>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          {orderData.shipping_address && orderData.shipping_address.street && (
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <MapPin size={20} className="text-purple-500" />
                <h3 className="text-lg font-semibold text-white">Adresse de livraison</h3>
              </div>
              <div className="text-sm text-gray-300">
                {orderData.shipping_address.street}<br />
                {orderData.shipping_address.city}, {orderData.shipping_address.postal_code}<br />
                {orderData.shipping_address.country}
              </div>
            </div>
          )}

          {/* Order Items */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Articles commandés</h3>
            <div className="space-y-3">
              {items && items.length > 0 ? items.map((item, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-gray-600 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center">
                      <Package size={20} className="text-gray-300" />
                    </div>
                    <div>
                      <div className="text-white font-medium">{item.name || item.product_name || item.product_name_fr}</div>
                      <div className="text-gray-400 text-sm">SKU: {item.sku || '-'}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white">
                      {item.quantity} × {Number(item.unit_price || item.price || 0).toFixed(2)} €
                    </div>
                    <div className="text-gray-400 text-sm">
                      {item.quantity ? (item.quantity * Number(item.unit_price || item.price || 0)).toFixed(2) : '0.00'} €
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-4 text-gray-400">
                  Aucun article trouvé
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-700">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Status Change Modal Component
const StatusChangeModal = ({ order, onClose, onStatusChange }) => {
  const [selectedStatus, setSelectedStatus] = useState(order?.status || 'pending');
  const [updating, setUpdating] = useState(false);

  const handleStatusChange = async () => {
    if (selectedStatus === order.status) {
      onClose();
      return;
    }

    setUpdating(true);
    try {
      await onStatusChange(order.id, selectedStatus);
      onClose();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const statusOptions = [
    { value: 'pending', label: 'En attente', color: 'bg-yellow-500' },
    { value: 'confirmed', label: 'Confirmée', color: 'bg-blue-500' },
    { value: 'processing', label: 'En traitement', color: 'bg-purple-500' },
    { value: 'shipped', label: 'Expédiée', color: 'bg-indigo-500' },
    { value: 'delivered', label: 'Livrée', color: 'bg-green-500' },
    { value: 'cancelled', label: 'Annulée', color: 'bg-red-500' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Changer le statut</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-300 text-sm mb-3">
            Commande <span className="font-medium text-white">#{order?.order_number}</span>
          </p>
          <p className="text-gray-400 text-xs">
            Statut actuel: <span className="font-medium text-white">{order?.status ? getStatusLabel(order.status) : order?.status}</span>
          </p>
        </div>

        <div className="space-y-2 mb-6">
          {statusOptions.map((status) => (
            <label
              key={status.value}
              className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                selectedStatus === status.value
                  ? 'bg-gray-700 border border-yellow-500'
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <input
                type="radio"
                name="status"
                value={status.value}
                checked={selectedStatus === status.value}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="sr-only"
              />
              <div className={`w-4 h-4 rounded-full border-2 border-gray-400 mr-3 flex items-center justify-center ${
                selectedStatus === status.value ? 'border-yellow-500' : ''
              }`}>
                {selectedStatus === status.value && (
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${status.color}`}></div>
                <span className="text-white font-medium">{status.label}</span>
              </div>
            </label>
          ))}
        </div>

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleStatusChange}
            disabled={updating || selectedStatus === order?.status}
            className="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {updating ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin mr-2" />
                Mise à jour...
              </>
            ) : (
              'Mettre à jour'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminOrdersPage; 